import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useProfileDiscovery } from "@/hooks/useProfileDiscovery";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

import BackgroundBalloons from "@/components/pop-balloon/BackgroundBalloons";
import ProfileBalloon from "@/components/pop-balloon/ProfileBalloon";
import UserIdentityCard from "@/components/pop-balloon/UserIdentityCard";
import ActionBar from "@/components/pop-balloon/ActionBar";
import PhotosModal from "@/components/pop-balloon/PhotosModal";
import ProfileModal from "@/components/pop-balloon/ProfileModal";
import VideoModal from "@/components/pop-balloon/VideoModal";
import MatchModal from "@/components/pop-balloon/MatchModal";
import VibeVideoNudge from "@/components/pop-balloon/VibeVideoNudge";
import SuperPopModal from "@/components/pop-balloon/SuperPopModal";
import WhoLikedMeModal from "@/components/profile/WhoLikedMeModal";
import PopBalloonHeader from "@/components/pop-balloon/PopBalloonHeader";
import HamburgerMenu from "@/components/pop-balloon/HamburgerMenu";

const PopBalloonPage = () => {
  const { t } = useTranslation();
  const { requestLocation } = useGeolocation();
  const [showWhoLikedMe, setShowWhoLikedMe] = useState(false);
  const [lastInteraction, setLastInteraction] = useState<"pop" | "like" | "match" | null>(null);

  // Callback to track interactions for video nudge
  const handleInteraction = useCallback((type: "pop" | "like" | "match") => {
    setLastInteraction(type);
  }, []);

  const {
    profiles,
    filteredProfiles,
    currentProfile,
    currentIndex,
    loading,
    isShaking,
    hasLocation,
    filters,
    updateFilters,
    resetFilters,
    showPhotos,
    setShowPhotos,
    showProfile,
    setShowProfile,
    showVideo,
    setShowVideo,
    showMatch,
    setShowMatch,
    showSuperPop,
    setShowSuperPop,
    
    setCurrentIndex,
    goToNext,
    pauseAutoPlay,
    handleTouchStart,
    handleTouchEnd,
    handleLike,
    handlePop,
    handleViewPhotos,
    handleViewProfile,
    handleWatchVideo,
    handleReport,
    handleBlock,
    handleStartChat,
    userProfile,
  } = useProfileDiscovery({ tabContext: "discovery", onInteraction: handleInteraction });

  const handleRequestLocation = async () => {
    const result = await requestLocation();
    if (result.success) {
      toast({ title: `📍 ${t("popBalloon.locationEnabled")}`, description: t("popBalloon.nowShowingDistances") });
    }
  };

  const headerProps = {
    filters,
    onFiltersChange: updateFilters,
    hasLocation,
    onRequestLocation: handleRequestLocation,
    onRestoreProfile: () => toast({ title: t("popBalloon.profileRestored"), description: t("popBalloon.lastSavedProfileRestored") }),
    onRestoreHiddenProfile: () => toast({ title: t("popBalloon.profileVisible"), description: t("popBalloon.profileNowVisible") }),
    onResetDiscoveryPreferences: () => {
      resetFilters();
      toast({ title: t("popBalloon.preferencesReset"), description: t("popBalloon.discoveryPreferencesReset") });
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen sky-gradient flex flex-col relative">
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
          <LanguageSwitcher position="top-right" className="relative" />
        </div>
        <PopBalloonHeader {...headerProps} />
        {/* Show hamburger menu even during loading so user can sign out */}
        <div className="fixed top-4 right-4 z-50">
          <HamburgerMenu />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full border-4 border-pop border-t-transparent animate-spin mx-auto" />
            <p className="mt-4 text-muted-foreground">{t("popBalloon.findingPeople")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (filteredProfiles.length === 0 && !loading) {
    return (
      <div className="min-h-screen sky-gradient flex flex-col pb-20 relative">
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
          <LanguageSwitcher position="top-right" className="relative" />
        </div>
        <PopBalloonHeader {...headerProps} />
        <BackgroundBalloons />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center z-10 px-4">
            <h2 className="text-2xl font-bold mb-2">
              {profiles.length === 0 ? t("popBalloon.expandingRadius") : t("popBalloon.noMatchesForFilters")}
            </h2>
            <p className="text-muted-foreground mb-4">
              {profiles.length === 0
                ? t("popBalloon.lookingForMore")
                : t("popBalloon.tryAdjustingFilters")}
            </p>
            {profiles.length > 0 && (
              <button
                onClick={resetFilters}
                className="px-4 py-2 rounded-xl bg-pop text-white font-medium"
              >
                {t("popBalloon.resetFilters")}
              </button>
            )}
            {profiles.length === 0 && (
              <div className="w-8 h-8 rounded-full border-4 border-pop border-t-transparent animate-spin mx-auto mt-6" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen sky-gradient overflow-hidden pb-20 flex flex-col">
      {/* Language Switcher - centered at top */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
        <LanguageSwitcher position="top-right" className="relative" />
      </div>
      
      {/* White header strip */}
      <PopBalloonHeader {...headerProps} />

      {/* Main content area */}
      <div
        className="relative flex-1 flex flex-col"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <BackgroundBalloons />

        <div className="relative z-10 flex flex-col flex-1">
          {/* Page title */}
          <div className="text-center py-2">
            <h1 className="text-xl font-bold text-foreground">{t("popBalloon.title")}</h1>
          </div>

          <div className="flex-1 flex items-center justify-center px-4 pb-4">
            {currentProfile && (
              <ProfileBalloon
                key={currentProfile.id}
                profile={currentProfile}
                onViewPhotos={handleViewPhotos}
                onViewProfile={handleViewProfile}
                onWatchVideo={handleWatchVideo}
                isShaking={isShaking}
              />
            )}
          </div>

          {/* User Identity Card */}
          {currentProfile && (
            <div className="py-2">
              <UserIdentityCard
                profile={currentProfile}
                onLike={handleLike}
                onPass={goToNext}
                onMessage={handleStartChat}
              />
            </div>
          )}

          <div className="pb-6 pt-2">
            <ActionBar
              onLike={handleLike}
              onWatchVideo={handleWatchVideo}
              onPop={handlePop}
              isPopping={isShaking}
            />
          </div>
        </div>

        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
          {filteredProfiles.slice(0, 10).map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                pauseAutoPlay();
                setCurrentIndex(idx);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? "bg-pop w-4" : "bg-white/60 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>

      {currentProfile && (
        <>
          <PhotosModal
            profile={currentProfile}
            isOpen={showPhotos}
            onClose={() => setShowPhotos(false)}
          />

          <ProfileModal
            profile={currentProfile}
            isOpen={showProfile}
            onClose={() => setShowProfile(false)}
            onReport={handleReport}
            onBlock={handleBlock}
          />

          <VideoModal
            profile={currentProfile}
            isOpen={showVideo}
            onClose={() => setShowVideo(false)}
          />

          <MatchModal
            profile={currentProfile}
            isOpen={showMatch}
            onClose={() => {
              setShowMatch(false);
              goToNext();
            }}
            onStartChat={handleStartChat}
            onViewProfile={() => {
              setShowMatch(false);
              setShowProfile(true);
            }}
            onWatchVideo={currentProfile.hasVideo ? () => {
              setShowMatch(false);
              setShowVideo(true);
            } : undefined}
          />
        </>
      )}

      {/* Gentle video encouragement - triggered by positive interactions */}
      <VibeVideoNudge userProfile={userProfile} triggerEvent={lastInteraction} />

      <WhoLikedMeModal
        isOpen={showWhoLikedMe}
        onClose={() => setShowWhoLikedMe(false)}
        onViewProfile={(profileId) => {
          const idx = filteredProfiles.findIndex((p) => p.id === profileId);
          if (idx >= 0) {
            setCurrentIndex(idx);
            setShowProfile(true);
          }
        }}
      />

      <SuperPopModal
        isOpen={showSuperPop}
        onClose={() => setShowSuperPop(false)}
        targetProfile={
          currentProfile
            ? { id: currentProfile.id, name: currentProfile.name, photo_url: currentProfile.photos[0] }
            : null
        }
        onSuccess={() => goToNext()}
      />
    </div>
  );
};

export default PopBalloonPage;
